import { useCallback } from 'react';
import { useApp } from '@context/AppContext';
import { useCorpse } from './useCorpse';
import { getZonePreset, getZoneHeight, ZONE_COLOUR_CLASSES } from './corpseAssignment';
import { Download } from 'lucide-react';

/**
 * Gallery view showing all completed creatures from the Exquisite Corpse session.
 * Each creature is displayed as a card with zone snapshots stacked vertically
 * and labels showing who drew each part. Supports 3, 4, or 5 zones.
 */
export default function CorpseGallery() {
  const { state } = useApp();
  const { game, user } = state;
  const { playAgain, endGame } = useCorpse();

  const totalCreatures = game.totalCreatures ?? 1;
  const creatures = game.creatures || {};
  const zoneCount = game.zoneCount || 3;
  const preset = getZonePreset(zoneCount);
  const zoneH = getZoneHeight(zoneCount);

  // Export a single creature as PNG
  const handleExport = useCallback((creatureIndex) => {
    const creature = creatures[creatureIndex];
    const fullSnapshot = creature?.fullSnapshot;
    if (!fullSnapshot) return;

    const link = document.createElement('a');
    link.download = `drawlab-corpse-creature-${creatureIndex + 1}-${Date.now()}.png`;
    link.href = fullSnapshot;
    link.click();
  }, [creatures]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 text-center">
          <h2 className="text-lg font-semibold text-charcoal font-display">
            <span className="mr-2">🧟</span>
            Creature Gallery
          </h2>
          <p className="text-xs text-slate-400 font-display mt-1">
            {totalCreatures} exquisite corpse{totalCreatures !== 1 ? 's' : ''} created
          </p>
        </div>

        {/* Scrollable gallery */}
        <div className="flex-1 overflow-y-auto p-5">
          <div className={`grid gap-4 ${totalCreatures > 1 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 max-w-sm mx-auto'}`}>
            {Array.from({ length: totalCreatures }).map((_, ci) => {
              const creature = creatures[ci];
              if (!creature) return null;

              return (
                <div key={ci} className="bg-slate-50 rounded-xl overflow-hidden border border-slate-100">
                  {/* Creature image */}
                  <div className="bg-white">
                    {creature.fullSnapshot ? (
                      <img
                        src={creature.fullSnapshot}
                        alt={`Creature ${ci + 1}`}
                        className="w-full h-auto block bg-canvas"
                      />
                    ) : (
                      /* Fallback: stack individual zone snapshots */
                      <div>
                        {preset.map((zone, zi) => {
                          const zoneData = creature.zones?.[zi];
                          return zoneData?.snapshot ? (
                            <img
                              key={zi}
                              src={zoneData.snapshot}
                              alt={`${zone.name} zone`}
                              className="w-full h-auto block bg-canvas"
                              style={{ aspectRatio: `2000 / ${Math.round(zoneH)}` }}
                            />
                          ) : (
                            <div
                              key={zi}
                              className="w-full bg-slate-100 flex items-center justify-center"
                              style={{ aspectRatio: `2000 / ${Math.round(zoneH)}` }}
                            >
                              <p className="text-slate-300 font-display text-xs">Empty</p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Creator credits */}
                  <div className="px-3 py-2.5 space-y-1">
                    <p className="text-xs text-slate-400 font-display uppercase tracking-wide mb-1.5">
                      Creature {ci + 1}
                    </p>
                    {preset.map((zone, zi) => {
                      const zoneData = creature.zones?.[zi];
                      if (!zoneData) return null;
                      const colours = ZONE_COLOUR_CLASSES[zone.colour];
                      return (
                        <div key={zi} className="flex items-center gap-2 text-xs font-display">
                          <span className={`font-medium ${colours.text}`}>
                            {zone.emoji} {zone.name}
                          </span>
                          <span className="text-slate-300">—</span>
                          <span className="text-charcoal">
                            {zoneData.assignedTo === user.id ? 'You' : zoneData.nickname}
                          </span>
                        </div>
                      );
                    })}

                    {/* Export button */}
                    {creature.fullSnapshot && (
                      <button
                        onClick={() => handleExport(ci)}
                        className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-500 text-xs font-display hover:bg-slate-200 transition-colors"
                      >
                        <Download className="w-3.5 h-3.5" />
                        Save PNG
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Host actions */}
        <div className="px-5 py-4 border-t border-slate-100 flex gap-3">
          {user.isHost ? (
            <>
              <button
                onClick={playAgain}
                className="flex-1 px-4 py-2.5 rounded-xl bg-amber text-white text-sm font-semibold font-display hover:bg-amber/90 transition-colors"
              >
                Play Again
              </button>
              <button
                onClick={endGame}
                className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-display text-slate-500 hover:bg-slate-50 transition-colors"
              >
                End Game
              </button>
            </>
          ) : (
            <div className="flex-1 px-4 py-2.5 rounded-xl bg-slate-50 text-sm font-display text-slate-400 text-center">
              Waiting for host...
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
